import React, { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { Search, Loader2, AlertCircle, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { saveGooglePlacesPhotos } from '@/lib/place-image-utils';

interface GooglePlacesSearchProps {
  onPlaceSelect: (placeData: any) => void;
}

interface GoogleConfig {
  apiKey: string;
  config: {
    loading: string;
    libraries: string;
    version: string;
    callback: string;
    photosWidth: number;
    photosHeight: number;
  }
}

const MAX_PHOTOS = 4;
const SCRIPT_LOAD_TIMEOUT = 10000; // 10 seconds timeout
const SCRIPT_ID = 'google-maps-script';

export function GooglePlacesSearch({ onPlaceSelect }: GooglePlacesSearchProps) {
  const [loading, setLoading] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const searchFieldsRef = useRef<string[]>([
    'name',
    'formatted_address',
    'place_id',
    'geometry',
    'photos',
    'types',
    'rating',
    'user_ratings_total',
    'opening_hours',
    'price_level'
  ]);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const scriptLoadAttempted = useRef(false);
  const scriptsInitialized = useRef(false);
  const configRef = useRef<GoogleConfig | null>(null);
  const { toast } = useToast();
  
  const cleanupFnRef = useRef<(() => void) | null>(null);
  
  useEffect(() => {
    if (!mapDivRef.current) {
      const div = document.createElement('div');
      div.style.display = 'none';
      div.id = 'map-div-for-places-api';
      document.body.appendChild(div);
      mapDivRef.current = div;
    }

    return () => {
      if (cleanupFnRef.current) {
        cleanupFnRef.current();
      }

      if (mapDivRef.current) {
        document.body.removeChild(mapDivRef.current);
        mapDivRef.current = null;
      }
    };
  }, []);

  const cleanupExistingScripts = () => {
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.remove();
    }
    
    if (window.initMap) {
      delete window.initMap;
    }
    
    scriptsInitialized.current = false;
    scriptLoadAttempted.current = false;
  };

  const loadScript = async () => {
    if (scriptLoading || scriptsInitialized.current) return;
    
    try {
      setScriptLoading(true);
      setError(null);
      scriptLoadAttempted.current = true;

      cleanupExistingScripts();

      await new Promise(resolve => setTimeout(resolve, 100));

      setStatusMessage('Preparing search functionality...');
      
      const { data, error: fetchError } = await supabase.functions.invoke('google-places-script');
      
      if (fetchError || !data) {
        console.error('Supabase function error:', fetchError);
        throw new Error('Search functionality is temporarily unavailable. Please try again later.');
      }
      
      if (!data?.apiKey) {
        console.error('Missing Google API key:', data);
        throw new Error('Search functionality is temporarily unavailable. Please try again later.');
      }

      configRef.current = data;
      console.log('Received API configuration');

      if (!document.head) {
        console.error('Document head not available');
        throw new Error('Cannot load scripts - document not ready');
      }
      
      setStatusMessage('Loading map services...');
      
      // Define global callback for Google Maps
      window.initMap = () => {
        console.log('Maps script loaded via callback');
        setTimeout(() => {
          if (window.google?.maps) {
            console.log('Maps ready with places library');
            scriptsInitialized.current = true;
            setScriptLoading(false);
            setStatusMessage(null);
            
            if (inputRef.current) {
              inputRef.current.focus();
            }
          } else {
            console.error('Google Maps object not available after load');
            setError('Search functionality is temporarily unavailable');
            setScriptLoading(false);
          }
        }, 500);
      };
      
      const mapsScript = document.createElement('script');
      mapsScript.type = 'text/javascript';
      mapsScript.defer = true;
      mapsScript.async = true;
      mapsScript.id = SCRIPT_ID;
      mapsScript.setAttribute('crossorigin', 'anonymous');
      
      const { apiKey, config } = configRef.current;
      mapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${config.libraries}&callback=initMap&v=${config.version}&loading=${config.loading}`;
      
      const scriptLoadPromise = new Promise<void>((resolve, reject) => {
        mapsScript.onload = () => {
          console.log('Maps script loaded via onload');
          resolve();
        };
        
        mapsScript.onerror = (error) => {
          console.error('Maps script failed to load:', error);
          reject(new Error('Failed to load maps script'));
        };
        
        document.head.appendChild(mapsScript);
        
        setTimeout(() => {
          if (!window.google?.maps) {
            reject(new Error('Maps script load timeout'));
          }
        }, SCRIPT_LOAD_TIMEOUT);
      });
      
      await scriptLoadPromise;
      
      cleanupFnRef.current = () => {
        cleanupExistingScripts();
      };
    } catch (error) {
      console.error('Error loading Google Maps:', error);
      setError(error instanceof Error ? error.message : 'Search functionality is temporarily unavailable');
      setScriptLoading(false);
      scriptLoadAttempted.current = false;
    }
  };

  useEffect(() => {
    if (!scriptsInitialized.current && !scriptLoadAttempted.current) {
      loadScript();
    }
    
    return () => {
      cleanupExistingScripts();
    };
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchFocus = () => {
    if (!scriptsInitialized.current) {
      loadScript();
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!searchQuery.trim()) {
      toast({
        title: "Error",
        description: "Please enter a search term",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!scriptsInitialized.current) {
        await loadScript();
        
        const waitStart = Date.now();
        while (!scriptsInitialized.current && Date.now() - waitStart < 5000) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!scriptsInitialized.current) {
          throw new Error('Maps API initialization timeout');
        }
      }

      if (!window.google?.maps?.places) {
        throw new Error('Google Maps Places API not available');
      }

      setStatusMessage('Searching for places...');
      
      try {
        if (!mapDivRef.current) {
          throw new Error('Map div not available');
        }
        
        const map = new google.maps.Map(mapDivRef.current);
        
        const placesService = new google.maps.places.PlacesService(map);
        
        const request = {
          query: searchQuery,
          fields: searchFieldsRef.current.slice()
        };
        
        placesService.findPlaceFromQuery(request, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
            placesService.getDetails(
              { 
                placeId: results[0].place_id,
                fields: searchFieldsRef.current.slice()
              },
              (placeResult, detailsStatus) => {
                if (detailsStatus === google.maps.places.PlacesServiceStatus.OK && placeResult) {
                  processPlaceDetails(placeResult);
                } else {
                  processPlaceDetails(results[0]);
                }
              }
            );
          } else {
            setError('No places found matching your search');
            setLoading(false);
            setStatusMessage(null);
          }
        });
      } catch (searchError) {
        console.error('Place search error:', searchError);
        throw new Error('Unable to find places matching your search. Please try another query.');
      }
      
    } catch (error) {
      console.error('Error searching place:', error);
      setError(error instanceof Error ? error.message : 'Search functionality is temporarily unavailable');
      setLoading(false);
      setStatusMessage(null);
    }
  };

  const processPlaceDetails = async (place: any) => {
    try {
      setStatusMessage('Processing place details...');
      console.log('Place details received:', place);
      
      if (place.photos) {
        console.log('Photos received:', place.photos.length);
        console.log('First photo object:', place.photos[0]);
      } else {
        console.log('No photos received from Google Places API');
      }
      
      const photoConfig = configRef.current?.config || { photosWidth: 1000, photosHeight: 1000 };
      
      let googlePhotoUrls: string[] = [];
      
      if (place.photos && place.photos.length > 0) {
        googlePhotoUrls = place.photos
          .slice(0, MAX_PHOTOS)
          .map((photo: any) => {
            try {
              if (typeof photo.getUrl === 'function') {
                const url = photo.getUrl({
                  maxWidth: photoConfig.photosWidth,
                  maxHeight: photoConfig.photosHeight
                });
                console.log('Generated photo URL:', url);
                return url;
              }
              console.warn('Photo object does not have getUrl function');
              return '';
            } catch (photoError) {
              console.error('Error getting photo URL:', photoError);
              return '';
            }
          })
          .filter(Boolean);
      }
      
      console.log('Processed photo URLs:', googlePhotoUrls);
      const placeName = place.name || 'Unknown Place';
      
      setStatusMessage('Saving photos to secure storage...');
      const photoUrls = await saveGooglePlacesPhotos(googlePhotoUrls, placeName);
      console.log('Saved photo URLs:', photoUrls);
      
      setStatusMessage(null);
      
      const address = place.formatted_address || '';
      const locationInfo = extractLocationFromAddress(address);
      
      let website = '';
      if (place.website) {
        website = place.website;
      }
      
      const placeData = {
        name: placeName,
        address: address,
        photos: photoUrls,
        url: website,
        rating: place.rating || null,
        rating_count: place.user_ratings_total || 0,
        price_range: place.price_level ? 
          ['budget', 'moderate', 'expensive', 'luxury'][place.price_level - 1] : null,
        place_type: determineType(place.types || []),
        requires_reservation: place.types?.includes('restaurant') || false,
        ...locationInfo,
        order_index: 0,
        amenities: [],
        accessibility_features: [],
        best_time_to_visit: null,
        tags: [],
        family_friendly: false,
        has_food: place.types?.includes('restaurant') || place.types?.includes('food') || false,
        categories: place.types || [],
        opening_hours: place.opening_hours ? {
          periods: place.opening_hours.periods,
          weekday_text: place.opening_hours.weekday_text
        } : null,
        cuisine_type: place.types?.find((type: string) => type.includes('cuisine')) || null,
        note: null,
        dress_code: null,
        capacity: null,
        ticket_price: null,
        start_date: null,
        end_date: null,
        frequency: null
      };

      setSelectedPlace(placeData);

      const handlePlaceSelect = async (place: any) => {
        const { data: similarPlaces } = await supabase
          .rpc('find_similar_places', { 
            place_name: place.name,
            place_address: address || ''
          });
        
        if (similarPlaces && similarPlaces.length > 0) {
          const existingPlace = similarPlaces[0];
          
          const mergedPlaceData = {
            ...placeData,
            ...existingPlace,
            original_place_id: existingPlace.original_place_id || existingPlace.id,
            address: existingPlace.address || address,
            photos: existingPlace.photos && existingPlace.photos.length > 0 
              ? existingPlace.photos 
              : placeData.photos,
            url: existingPlace.url || placeData.url,
            isExistingPlace: true
          };
          
          onPlaceSelect(mergedPlaceData);
        } else {
          onPlaceSelect(placeData);
        }
      };

      handlePlaceSelect(placeData);
    } catch (error) {
      console.error('Error processing place details:', error);
      setError(error instanceof Error ? error.message : 'Failed to process place details');
    } finally {
      setLoading(false);
    }
  };

  const determineType = (types: string[]): string => {
    if (types.includes('lodging') || types.includes('hotel')) return 'hotel';
    if (types.includes('restaurant')) return 'restaurant';
    if (types.includes('store') || types.includes('shop')) return 'shop';
    if (types.includes('museum') || types.includes('gallery')) return 'attraction';
    if (types.includes('amusement_park') || types.includes('zoo')) return 'attraction';
    if (types.includes('park') || types.includes('natural_feature')) return 'attraction';
    return 'activity';
  };

  const extractLocationFromAddress = (address: string) => {
    const parts = address.split(', ');
    return {
      city: parts[parts.length - 3] || '',
      country: parts[parts.length - 1] || ''
    };
  };

  const handleRetry = () => {
    setError(null);
    scriptLoadAttempted.current = false;
    scriptsInitialized.current = false;
    cleanupExistingScripts();
    loadScript();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchQuery(e.target.value);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSearch(e as any);
  };

  return (
    <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="space-y-4">
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              ref={inputRef}
              className="pl-10"
              placeholder="Search for a place..."
              disabled={loading || scriptLoading}
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleSearchFocus}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSearch(e as any);
                }
              }}
            />
          </div>
          <Button 
            type="button" 
            onClick={handleButtonClick}
            disabled={loading || scriptLoading}
          >
            {loading || scriptLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Search"
            )}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Temporarily Unavailable</AlertTitle>
          <AlertDescription className="flex flex-col space-y-2">
            <span>Our search functionality is temporarily unavailable. Please try again later.</span>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 w-fit flex items-center gap-2" 
              onClick={handleRetry}
            >
              <RefreshCcw className="h-4 w-4" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {statusMessage && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {selectedPlace && selectedPlace.photos && selectedPlace.photos.length > 0 && (
        <Card className="mt-4">
          <CardContent className="p-4">
            <Carousel>
              <CarouselContent>
                {selectedPlace.photos.map((photo: string, index: number) => (
                  <CarouselItem key={index}>
                    <div className="aspect-video relative">
                      <img
                        src={photo}
                        alt={`${selectedPlace.name} - Photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default GooglePlacesSearch;
